import { PatientData } from './patients'

/**
 * Calendar event types
 */

export enum EVENT_TYPES {
	CALL = 1,
	APPOINTMENT,
	METTING,
	WEBINAR,
	GROUP_SESSION,
	PT_SESSION,
	VIDEO,
}

export enum EVENT_CATEGORY_COLORS {
	CATEGORY_0 = 'lightgray',
	CATEGORY_1 = '#cfbed7',
	CATEGORY_2 = '#ced0c6',
	CATEGORY_3 = '#90b6b1',
	CATEGORY_4 = '#eeceb0',
	CATEGORY_5 = '#cfbed7',
	CATEGORY_6 = '#e7cb8d',
	CATEGORY_7 = '#b2c8e1',
}

export enum EVENT_CATEGORY_VALUE {
	CATEGORY_0 = 'CATEGORY_0',
	CATEGORY_1 = 'CATEGORY_1',
	CATEGORY_2 = 'CATEGORY_2',
	CATEGORY_3 = 'CATEGORY_3',
	CATEGORY_4 = 'CATEGORY_4',
	CATEGORY_5 = 'CATEGORY_5',
	CATEGORY_6 = 'CATEGORY_6',
	CATEGORY_7 = 'CATEGORY_7',
}

export enum EVENT_STATUS {
	SCHEDULED = 1,
	RESCHEDULED,
	CANCELLED,
	COMPLETED,
	MISSED,
	DISCOVERY_COMPLETED,
}

export interface CalendarEventStatusConfig {
	id: EVENT_STATUS
	value: keyof typeof EVENT_STATUS
}

export interface CalendarEventTypeConfig {
	id: EVENT_TYPES
	value: keyof typeof EVENT_TYPES
}

export interface CalendarEventCategoryConfig {
	id: string
	value: EVENT_CATEGORY_VALUE
	color: EVENT_CATEGORY_COLORS
	fontColor: string
}

export interface CalendarEventCategoryConfigV2 {
	id: string
	value: string
}

interface CalendarEventBaseDetails {
	['patients_ID']: Array<string>
	type: number
	timestamp: number
	['start_time']: number
	['end_time']: number
	['reminder_alert']: number
	['formatted_start_time']: string
	typeName: string
	video: boolean
	location: string
}

interface CalendarEventBaseDescription {
	title: string
	category?: number
	status: EVENT_STATUS
	text: string
}

interface CalendarEventBaseExtra {
	coachID: string
}

export interface CalendarEventBase {
	details: CalendarEventBaseDetails
	description: CalendarEventBaseDescription
	extra: CalendarEventBaseExtra
}

export interface CreateCalendarEventReqData
	extends Omit<CalendarEventBase, 'details'> {
	details: CreateCalendarEventReqDataDetails
}

export interface CreateCalendarEventReqDataDetails
	extends Omit<
		CalendarEventBaseDetails,
		'start_time' | 'end_time' | 'formatted_start_time'
	> {
	['start_time']?: number
	['end_time']?: number
	['formatted_start_time']?: string
}

export interface CalendarEventPatientData {
	patient: PatientData
}

interface CalendarEventDetails<P>
	extends Omit<
		CalendarEventBaseDetails,
		| 'patients_ID'
		| 'formatted_start_time'
		| 'typeName'
		| 'video'
		| 'location'
	> {
	['patients_ID']: P
	location?: string
	video?: boolean
}

interface CalendarEventDescription
	extends Omit<CalendarEventBaseDescription, 'text'> {
	text?: string
}
interface CalendarEventAppMessage {
	body: string
	title: string
}

interface CalendarEventExtra extends CalendarEventBaseExtra {
	zoomRecordings: []
}

export interface CalendarEvent<P = string> {
	_id: string
	coachName: string
	details: CalendarEventDetails<P>
	description: CalendarEventDescription
	extra: CalendarEventExtra
	op: {
		created: string
		modified: string
	}
	appMessage: CalendarEventAppMessage
	patientsData: Array<CalendarEventPatientData>
}

/**
 * Calendar monthly request data type
 */

export interface CalendarEventRequestData {
	date?: number
	start?: number
	end?: number
	view?: string | null
}

/**
 * Calendar data types: types for calendar data, calendar day change & set function
 */

export interface CalendarDayData {
	d: number | string
	hasEvent: boolean
}

export type CalendarData = Array<Array<CalendarDayData>>

export type CalendarChangeTimeFunc = (time: number) => void

export type CalendarMode = 'day' | 'week' | 'month' | 'list'

export interface CalendarModeItem {
	id: CalendarMode
	label: string
}
